/**
 * Cart Reservation API Client - Backend Only
 * Provides backend API communication with comprehensive debugging
 * 
 * ENDPOINT BEHAVIOR:
 * - POST /api/reservations       → ADDS quantity to existing reservation
 * - POST /api/reservations/sync  → COMPLETELY OVERRIDES quantities (replaces all)
 */

class CartReservationAPI {
  constructor() {
    this.baseUrl = window.reservationAPI?.baseUrl || 'http://localhost:3001';
    
    console.log('═══════════════════════════════════════════════════════════');
    console.log('🔧 [Reservation API] BACKEND-ONLY MODE INITIALIZED');
    console.log(`🔧 [Reservation API] Base URL: ${this.baseUrl}`);
    console.log('═══════════════════════════════════════════════════════════');
    
    // Test connectivity on init
    this.testConnection();
  }
  
  /**
   * Test backend connectivity
   */
  async testConnection() {
    try {
      console.log('🔌 [Reservation API] Testing backend connection...');
      console.log(`   URL: ${this.baseUrl}/api/health`);
      
      const response = await fetch(`${this.baseUrl}/api/health`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('═══════════════════════════════════════════════════════════');
        console.log('✅ [Reservation API] BACKEND CONNECTED SUCCESSFULLY!');
        console.log('   Status:', data.status);
        console.log('   Active Reservations:', data.reservations);
        console.log('   Active Customers:', data.activeCustomers);
        console.log('═══════════════════════════════════════════════════════════');
      } else {
        console.error('═══════════════════════════════════════════════════════════');
        console.error(`❌ [Reservation API] BACKEND ERROR: ${response.status}`);
        console.error('═══════════════════════════════════════════════════════════');
      }
    } catch (error) {
      console.error('═══════════════════════════════════════════════════════════');
      console.error('❌ [Reservation API] BACKEND CONNECTION FAILED!');
      console.error('   Error:', error.message);
      console.error('   URL:', this.baseUrl);
      console.error('   ⚠️  Make sure backend server is running!');
      console.error('   Run: cd cart-reservation-backend && node server.js');
      console.error('═══════════════════════════════════════════════════════════');
    }
  }

  /**
   * Get reserved quantity for a variant
   * GET /api/reservations/:variantId?excludeEmail={email}
   */
  async getReservedQuantity(variantId, excludeEmail) {
    console.log(`📊 [Reservation API] Getting reserved quantity`);
    console.log(`   Variant ID: ${variantId}`);
    console.log(`   Excluding: ${excludeEmail || 'none'}`);
    
    if (!variantId) {
      console.error('❌ Missing variantId - cannot check reservations');
      throw new Error('Variant ID is required');
    }
    
    try {
      const queryParams = [];
      
      if (excludeEmail) {
        queryParams.push(`excludeEmail=${encodeURIComponent(excludeEmail)}`);
      }
      
      const url = `${this.baseUrl}/api/reservations/${variantId}${queryParams.length > 0 ? '?' + queryParams.join('&') : ''}`;
      console.log(`   🌐 API Call: GET ${url}`);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log(`   ✅ Response:`, data);
      console.log(`   📦 Total reserved: ${data.totalReserved}`);
      
      return data.totalReserved || 0;
    } catch (error) {
      console.error('❌ [Reservation API] Failed to get reserved quantity:', error);
      console.error('   Variant ID:', variantId);
      // Fail-closed: throw error so frontend can block add-to-cart
      throw new Error(`Failed to verify reservation availability: ${error.message}`);
    }
  }

  /**
   * Create or update a reservation (ADDS to existing quantity in DB)
   * POST /api/reservations
   * Body: { variantId, quantity, customerEmail, inventoryLimit? }
   * 
   * NOTE: This endpoint ADDS the quantity to any existing reservation.
   * Use syncCart() to completely override quantities.
   */
  async createReservation(variantId, quantity, customerEmail, inventoryLimit = null) {
    console.log(`🔒 [Reservation API] Creating reservation (ADD to existing):`);
    console.log(`   Variant: ${variantId}`);
    console.log(`   Quantity to ADD: ${quantity}`);
    console.log(`   Customer: ${customerEmail}`);
    console.log(`   Inventory Limit: ${inventoryLimit || 'N/A'}`);
    
    try {
      const url = `${this.baseUrl}/api/reservations`;
      const body = { 
        variantId: variantId.toString(), 
        quantity: parseInt(quantity), 
        customerEmail,
        inventoryLimit: inventoryLimit ? parseInt(inventoryLimit) : null
      };
      
      console.log(`   🌐 API Call: POST ${url}`);
      console.log(`   📦 Body:`, body);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        
        if (errorData.violations) {
          console.error('═══════════════════════════════════════════════════════════');
          console.error('❌ [Reservation API] SERVER REJECTED - Inventory violations!');
          console.error('   Violations:', errorData.violations);
          
          // Log details
          errorData.violations.forEach(violation => {
            console.error(`   Variant ${violation.variantId}:`);
            console.error(`      Requested: ${violation.requestedQuantity}`);
            console.error(`      Total Reserved: ${violation.totalReserved}`);
            console.error(`      Inventory Limit: ${violation.inventoryLimit}`);
            console.error(`      Exceeded by: ${violation.exceeded}`);
          });
          console.error('═══════════════════════════════════════════════════════════');
        }
        
        // Create error with violations attached
        const error = new Error(`HTTP ${response.status}: ${errorData.error || 'Reservation failed'}`);
        error.violations = errorData.violations;
        error.errorData = errorData;
        throw error;
      }
      
      const data = await response.json();
      console.log('═══════════════════════════════════════════════════════════');
      console.log('✅ [Reservation API] RESERVATION CREATED');
      console.log(`   Variant: ${data.variantId}`);
      console.log(`   Quantity: ${data.quantity}`);
      console.log(`   Customer: ${data.customerEmail}`);
      console.log(`   Expires At: ${new Date(data.expiresAt).toLocaleTimeString()}`);
      console.log('═══════════════════════════════════════════════════════════');
      
      return data;
    } catch (error) {
      console.error('❌ [Reservation API] Failed to create reservation:', error);
      console.error('   Variant:', variantId);
      console.error('   Quantity:', quantity);
      console.error('   Customer:', customerEmail);
      throw error;
    }
  }

  /**
   * Delete all reservations for a customer
   */
  async deleteCustomerReservations(customerEmail) {
    console.log(`🗑️ [Reservation API] Deleting reservations for: ${customerEmail}`);
    
    try {
      const url = `${this.baseUrl}/api/reservations/${encodeURIComponent(customerEmail)}`;
      console.log(`   🌐 API Call: DELETE ${url}`);
      
      const response = await fetch(url, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('   ✅ Reservations deleted:', data);
      
      return data;
    } catch (error) {
      console.error('❌ [Reservation API] Failed to delete reservations:', error);
      throw error;
    }
  }

  /**
   * Get customer timer
   */
  async getTimer(customerEmail) {
    console.log(`⏱️ [Reservation API] Getting timer for: ${customerEmail}`);
    
    try {
      const url = `${this.baseUrl}/api/timer/${encodeURIComponent(customerEmail)}`;
      console.log(`   🌐 API Call: GET ${url}`);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('   ✅ Timer response:', data);
      
      if (!data.hasTimer) {
        console.log('   ℹ️ No active timer for this customer');
        return null;
      }
      
      // Calculate expiry timestamp using client's clock + timeRemaining from server
      // This avoids timezone parsing issues with the server's expiresAt string
      let expiryTimestamp;
      if (data.timeRemaining && data.timeRemaining > 0) {
        // Use timeRemaining (in milliseconds) from server to calculate local expiry
        expiryTimestamp = Date.now() + data.timeRemaining;
        console.log(`   🔄 Calculated expiry from timeRemaining: ${data.timeRemaining}ms → ${new Date(expiryTimestamp).toLocaleTimeString()}`);
      } else if (typeof data.expiresAt === 'number') {
        // If expiresAt is already a timestamp number, use it directly
        expiryTimestamp = data.expiresAt;
        console.log(`   🔄 Using numeric expiresAt: ${expiryTimestamp}`);
      } else {
        // Fallback: parse the ISO string (may have timezone issues)
        expiryTimestamp = new Date(data.expiresAt).getTime();
        console.log(`   ⚠️ Fallback: parsed ISO string ${data.expiresAt} → ${expiryTimestamp}`);
      }
      
      const timer = {
        expiryTimestamp: expiryTimestamp,
        customerEmail: customerEmail
      };
      
      console.log(`   ⏰ Timer expires at: ${new Date(timer.expiryTimestamp).toLocaleTimeString()}`);
      console.log(`   ⏰ Timestamp: ${timer.expiryTimestamp}`);
      
      return timer;
    } catch (error) {
      console.error('❌ [Reservation API] Failed to get timer:', error);
      return null; // Return null on error, don't throw
    }
  }

  /**
   * Update customer timer
   */
  async updateTimer(customerEmail, expiryTimestamp) {
    console.log(`⏱️ [Reservation API] Updating timer for: ${customerEmail}`);
    console.log(`   Expires at: ${new Date(expiryTimestamp).toLocaleTimeString()}`);
    
    try {
      const url = `${this.baseUrl}/api/timer`;
      const body = { customerEmail };
      
      console.log(`   🌐 API Call: POST ${url}`);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('   ✅ Timer updated:', data);
      
      return data;
    } catch (error) {
      console.error('❌ [Reservation API] Failed to update timer:', error);
      throw error;
    }
  }

  /**
   * Sync cart items with reservations (completely overrides quantities in DB)
   * POST /api/reservations/sync
   * Body: { customerEmail, cartItems: [{ variantId, quantity, inventoryLimit }] }
   * 
   * Response on success:
   * {
   *   success: true,
   *   customerEmail: string,
   *   syncedItems: [{ variantId, requestedQuantity, totalReserved, inventoryLimit, exceeded }],
   *   removedItems: number,
   *   expiresAt: timestamp
   * }
   */
  async syncCart(customerEmail, cartItems) {
    console.log(`🔄 [Reservation API] Syncing cart for: ${customerEmail}`);
    console.log(`   Items to sync:`, cartItems);
    
    try {
      // Prepare cart items according to spec: only variantId, quantity, inventoryLimit
      const syncCartItems = cartItems.map(item => ({
        variantId: item.variantId,
        quantity: item.quantity,
        inventoryLimit: item.inventoryLimit || null
      }));
      
      const url = `${this.baseUrl}/api/reservations/sync`;
      const body = { 
        customerEmail, 
        cartItems: syncCartItems 
      };
      
      console.log(`   🌐 API Call: POST ${url}`);
      console.log(`   📦 Payload:`, body);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        
        if (errorData.violations) {
          console.error('═══════════════════════════════════════════════════════════');
          console.error('❌ [Reservation API] SERVER REJECTED - Inventory violations!');
          console.error('   Violations:', errorData.violations);
          console.error('═══════════════════════════════════════════════════════════');
        }
        
        // Create error with violations attached
        const error = new Error(`HTTP ${response.status}: ${errorData.error || 'Sync failed'}`);
        error.violations = errorData.violations; // Attach violations to error object
        error.errorData = errorData;
        throw error;
      }
      
      const data = await response.json();
      console.log('═══════════════════════════════════════════════════════════');
      console.log('✅ [Reservation API] SYNC SUCCESSFUL');
      console.log(`   Customer: ${data.customerEmail}`);
      console.log(`   Synced Items: ${data.syncedItems?.length || 0}`);
      console.log(`   Removed Items: ${data.removedItems}`);
      console.log(`   Expires In: ${data.expiresIn !== undefined ? Math.floor(data.expiresIn / 60) + 'm ' + (data.expiresIn % 60) + 's' : 'N/A'} (${data.expiresIn}s)`);
      
      // Log details for each synced item
      if (data.syncedItems && data.syncedItems.length > 0) {
        console.log('   📊 Item Details:');
        data.syncedItems.forEach(item => {
          const totalAfter = item.totalReserved + item.requestedQuantity;
          const available = item.inventoryLimit - item.totalReserved;
          console.log(`      Variant ${item.variantId}:`);
          console.log(`         Requested: ${item.requestedQuantity}`);
          console.log(`         Total Reserved (by others, excluding me): ${item.totalReserved}`);
          console.log(`         Inventory Limit: ${item.inventoryLimit}`);
          console.log(`         Total After Add: ${totalAfter}`);
          console.log(`         Available for others: ${available}`);
          console.log(`         Exceeded: ${item.exceeded || 0}`);
        });
      }
      console.log('═══════════════════════════════════════════════════════════');
      
      // CRITICAL: Validate response even if HTTP 200
      // Backend might return success=true but with violations in syncedItems
      if (data.syncedItems && Array.isArray(data.syncedItems)) {
        const violations = [];
        data.syncedItems.forEach(item => {
          // Check if exceeded is positive (means violation)
          if (item.exceeded && item.exceeded > 0) {
            violations.push({
              variantId: item.variantId,
              requestedQuantity: item.requestedQuantity,
              totalReserved: item.totalReserved,
              inventoryLimit: item.inventoryLimit,
              exceeded: item.exceeded
            });
          }
          // Also check if totalReserved + requestedQuantity > inventoryLimit
          else if (item.inventoryLimit && item.totalReserved !== undefined && item.requestedQuantity !== undefined) {
            const totalAfter = item.totalReserved + item.requestedQuantity;
            if (totalAfter > item.inventoryLimit) {
              violations.push({
                variantId: item.variantId,
                requestedQuantity: item.requestedQuantity,
                totalReserved: item.totalReserved,
                inventoryLimit: item.inventoryLimit,
                exceeded: totalAfter - item.inventoryLimit
              });
            }
          }
        });
        
        if (violations.length > 0) {
          console.error('═══════════════════════════════════════════════════════════');
          console.error('❌ [Reservation API] VIOLATIONS DETECTED IN RESPONSE!');
          console.error('   Even though HTTP 200, violations found:');
          violations.forEach(v => {
            console.error(`      Variant ${v.variantId}: ${v.requestedQuantity} requested, ${v.totalReserved} reserved, limit ${v.inventoryLimit}, exceeded by ${v.exceeded}`);
          });
          console.error('═══════════════════════════════════════════════════════════');
          
          // Create error with violations attached
          const error = new Error('Inventory limit exceeded');
          error.violations = violations;
          error.errorData = data;
          throw error;
        }
      }
      
      return data;
    } catch (error) {
      console.error('❌ [Reservation API] Failed to sync cart:', error);
      throw error;
    }
  }
  
  /**
   * Get system stats (debugging)
   */
  async getStats() {
    console.log('📊 [Reservation API] Getting system stats...');
    
    try {
      const url = `${this.baseUrl}/api/stats`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📊 Backend Stats:', data);
      
      return data;
    } catch (error) {
      console.error('❌ [Reservation API] Failed to get stats:', error);
      return null;
    }
  }
}

// Initialize API client
window.cartReservationAPI = new CartReservationAPI();

console.log('═══════════════════════════════════════════════════════════');
console.log('✅ [Reservation API] Backend-Only Client Loaded');
console.log('💡 Debug Commands:');
console.log('   window.cartReservationAPI.getStats()  - View all reservations');
console.log('   window.cartReservationManager.getState() - View system state');
console.log('═══════════════════════════════════════════════════════════');
